<template>
  <div id="avaliacao-quadrimestral">
    <Menu />

    <div class="container main">
      <div class="row">
        <div class="col-md-12">
          <SectionTitle
            title="Avaliação quadrimestral"
            description="Importar classificação por equipe em Vínculo, Acompanhamento e Indicadores de Qualidade"
            url="/administracao/importar-dados"
            :enabledModalInformacao="true"
            :enabledIframeModalInformacao="true"
            urlIframeModalInformacao="https://doc.clickup.com/9011510980/d/h/8cj1fp4-1631/599ea88448ce3d9/8cj1fp4-5611"
            pageIdentifier="importar-dados-avaliacao-quadrimestral"
            :enableBadgeModalInformacao="
              Number(
                $store.state.permissoesRaw
                  ?.importar_dados_avaliacao_quadrimestral_ajuda || 0
              ) > 0
            "
            :badgeValueModalInformacao="
              $store.state.permissoesRaw
                ?.importar_dados_avaliacao_quadrimestral_ajuda || 0
            "
            keyAjudaModalInformacao="importar_dados_avaliacao_quadrimestral_ajuda"
          />
        </div>

        <div class="col-md-12">
          <div class="card mb-4">
            <div class="card-body">
              <h5 class="mb-3">Qualidade</h5>

              <b-form-file
                v-model="arquivoQualidade"
                browse-text="Selecionar"
                placeholder="Selecione o arquivo Qualidade..."
                drop-placeholder="Solte o arquivo aqui..."
                accept=".xls,.xlsx,.csv"
                @input="previewArquivoQualidade"
              ></b-form-file>

              <div v-if="previewQualidade.length" class="table-responsive mt-4">
                <b-alert show variant="info" class="mb-3">
                  <i class="icofont-info-circle mr-2"></i>
                  Esta prévia exibe apenas <strong>2 registros</strong> do
                  arquivo para conferência. Todos os registros serão importados.
                </b-alert>

                <h6>Prévia do arquivo</h6>

                <table class="table table-sm table-bordered table-preview">
                  <thead>
                    <tr>
                      <th v-for="(coluna, i) in previewQualidade[0]" :key="i">
                        {{ coluna }}
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    <tr
                      v-for="(linha, index) in previewQualidade.slice(1)"
                      :key="index"
                    >
                      <td v-for="(coluna, i) in linha" :key="i">
                        {{ coluna }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <button
                class="btn btn-primary mt-3"
                :disabled="isLoadingQualidade || !previewQualidade.length"
                @click="importarQualidade"
              >
                <template v-if="isLoadingQualidade">
                  <b-spinner small class="mr-2"></b-spinner>
                  Importando Qualidade...
                </template>

                <template v-else>Importar Qualidade</template>
              </button>
            </div>
          </div>

          <div class="card mb-4">
            <div class="card-body">
              <h5 class="mb-3">CVAT</h5>

              <b-form-file
                v-model="arquivoCvat"
                browse-text="Selecionar"
                placeholder="Selecione o arquivo CVAT..."
                drop-placeholder="Solte o arquivo aqui..."
                accept=".xls,.xlsx,.csv"
                @input="previewArquivoCvat"
              ></b-form-file>

              <div v-if="previewCvat.length" class="table-responsive mt-4">
                <b-alert show variant="info" class="mb-3">
                  <i class="icofont-info-circle mr-2"></i>
                  Esta prévia exibe apenas <strong>2 registros</strong> do
                  arquivo para conferência. Todos os registros serão importados.
                </b-alert>

                <h6>Prévia do arquivo</h6>

                <table class="table table-sm table-bordered table-preview">
                  <thead>
                    <tr>
                      <th v-for="(coluna, i) in previewCvat[0]" :key="i">
                        {{ coluna }}
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    <tr
                      v-for="(linha, index) in previewCvat.slice(1)"
                      :key="index"
                    >
                      <td v-for="(coluna, i) in linha" :key="i">
                        {{ coluna }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <button
                class="btn btn-primary mt-3"
                :disabled="isLoadingCvat || !previewCvat.length"
                @click="importarCvat"
              >
                <template v-if="isLoadingCvat">
                  <b-spinner small class="mr-2"></b-spinner>
                  Importando CVAT...
                </template>

                <template v-else>Importar CVAT</template>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <Footer />
  </div>
</template>

<script>
import * as XLSX from 'xlsx'
import Menu from '@/components/Menu'
import Footer from '@/components/Footer'
import SectionTitle from '@/components/SectionTitle'

export default {
  metaInfo: {
    title: 'Avaliação quadrimestral',
    titleTemplate: null,
  },
  components: {
    Menu,
    Footer,
    SectionTitle,
  },
  data() {
    return {
      arquivoCvat: null,
      arquivoQualidade: null,
      previewCvat: [],
      previewQualidade: [],
      isLoadingCvat: false,
      isLoadingQualidade: false,
    }
  },
  methods: {
    /**
     * Lê o arquivo selecionado e retorna as linhas da primeira aba.
     *
     * @param {File} arquivo Arquivo selecionado.
     * @returns {Promise<Array>}
     */
    lerArquivo(arquivo) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()

        reader.onload = (event) => {
          try {
            const data = new Uint8Array(event.target.result)
            const workbook = XLSX.read(data, { type: 'array' })
            const sheetName = workbook.SheetNames[0]
            const worksheet = workbook.Sheets[sheetName]

            const linhas = XLSX.utils.sheet_to_json(worksheet, {
              header: 1,
              defval: '',
            })

            resolve(linhas)
          } catch (error) {
            reject(error)
          }
        }

        reader.onerror = reject
        reader.readAsArrayBuffer(arquivo)
      })
    },

    /**
     * Verifica se o arquivo possui a estrutura esperada do CVAT.
     *
     * A célula J16 representa:
     * - linha 16 => índice 15
     * - coluna J => índice 9
     *
     * @param {Array} linhas Linhas da planilha.
     * @returns {Boolean}
     */
    arquivoEhCvat(linhas) {
      const valorJ16 = String(linhas?.[15]?.[9] || '')
        .trim()
        .toLowerCase()

      return valorJ16 === 'dimensão' || valorJ16 === 'dimensao'
    },

    /**
     * Monta a prévia da planilha.
     *
     * @param {Array} linhas Linhas da planilha.
     * @param {Number} linhaCabecalho Índice da linha onde começa o cabeçalho.
     * @returns {Array}
     */
    montarPreview(linhas, linhaCabecalho = 15) {
      return linhas
        .slice(linhaCabecalho, linhaCabecalho + 3)
        .map((linha) => linha.slice(0, 16))
        .filter((linha) => linha.some((coluna) => String(coluna).trim() !== ''))
    },

    /**
     * Gera a prévia do arquivo de Qualidade.
     */
    async previewArquivoQualidade() {
      this.previewQualidade = []

      if (!this.arquivoQualidade) return

      try {
        const linhas = await this.lerArquivo(this.arquivoQualidade)

        if (this.arquivoEhCvat(linhas)) {
          this.arquivoQualidade = null
          this.$toast?.error?.(
            'Arquivo inválido. Selecione um arquivo de Qualidade.'
          )
          return
        }

        this.previewQualidade = this.montarPreview(linhas, 15)
      } catch (error) {
        console.error(error)
        this.$toast?.error?.('Erro ao ler o arquivo de Qualidade.')
      }
    },

    /**
     * Gera a prévia do arquivo CVAT.
     */
    async previewArquivoCvat() {
      this.previewCvat = []

      if (!this.arquivoCvat) return

      try {
        const linhas = await this.lerArquivo(this.arquivoCvat)

        if (!this.arquivoEhCvat(linhas)) {
          this.arquivoCvat = null
          this.$toast?.error?.(
            'Arquivo inválido. Selecione um arquivo do CVAT.'
          )
          return
        }

        this.previewCvat = this.montarPreview(linhas, 15)
      } catch (error) {
        console.error(error)
        this.$toast?.error?.('Erro ao ler o arquivo CVAT.')
      }
    },

    /**
     * Importa o arquivo do CVAT.
     */
    async importarCvat() {
      if (!this.arquivoCvat) {
        this.$toast?.error?.('Selecione o arquivo CVAT.')
        return
      }

      const formData = new FormData()
      formData.append('co_ibge', this.$store.state.cod_ibge)
      formData.append('cvat', this.arquivoCvat)

      this.isLoadingCvat = true

      try {
        await this.$api.post(
          '/app/avaliacao-quadrimestral/importar-cvat',
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        )

        this.$toast?.success?.('Arquivo CVAT importado com sucesso.')
      } catch (error) {
        console.error(error)
        this.$toast?.error?.('Erro ao importar o arquivo CVAT.')
      } finally {
        this.isLoadingCvat = false
      }
    },

    /**
     * Importa o arquivo do Qualidade.
     */
    async importarQualidade() {
      if (!this.arquivoQualidade) {
        this.$toast?.error?.('Selecione o arquivo Qualidade.')
        return
      }

      const formData = new FormData()
      formData.append('co_ibge', this.$store.state.cod_ibge)
      formData.append('qualidade', this.arquivoQualidade)

      this.isLoadingQualidade = true

      try {
        await this.$api.post(
          '/app/avaliacao-quadrimestral/importar-qualidade',
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        )

        this.$toast?.success?.('Arquivo Qualidade importado com sucesso.')
      } catch (error) {
        console.error(error)
        this.$toast?.error?.('Erro ao importar o arquivo Qualidade.')
      } finally {
        this.isLoadingQualidade = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
#avaliacao-quadrimestral {
  .table-responsive {
    display: inherit;
    margin-bottom: 0px;
  }
}
</style>
